import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import PersonIcon from '@material-ui/icons/Person';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useStyles } from './styles';
import { CircularProgress, Paper, Tooltip, Snackbar, SnackbarContent } from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { toFormatDateTime } from 'utils';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, InputAdornment, IconButton, Button } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { GridSelectionModelChangeParams } from '@material-ui/data-grid';
import { useCallback } from 'react';
import { debounce } from 'lodash';
import { Autocomplete } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { SyncDepartmentsView } from './types';

export const SyncUser: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  // const [hasNextPage, setHasNextPage] = useState(false);
  const [pageTokens, setPageTokens] = useState<string[]>(['']); // Track tokens for each page
  const [cachedPages, setCachedPages] = useState<{ [key: number]: any[] }>({ 0: [] }); // Cache page data

  const {
    googleStore: {
      syncUser,
      syncuserView,
      syncToken,
      reSyncUser,
      reSyncSingleUser,
      SelectedUser,
      syncDepartment,
      allsyncDepartmentsView,
      totalUserCount,
      currentPageSize,
      hasNextPage: apiHasNextPage,
      maxResults,
    },
    adminsTableStore: { getAdmins, adminsView },
    dashboardStore: { generalStatistic, getStatistic },
    ldapStore: { usersView, getUsers },
    notificationStore: { openNotification },
  } = useStore();

  const [pageSize, setPageSize] = useState(currentPageSize || 10);
  const getAdminUsersReq = useQuery('getAdminUsersReq', () => getAdmins(), {
    onError: () => openNotification('error', `Can't get admin users`),
  });
  const { control } = useForm<SyncDepartmentsView>();
  const syncUserReq = useQuery(
    ['syncUser', nextPageToken, search, department, currentPage, refreshKey],
    () => {
      return syncUser(nextPageToken, search, department);
    },
    {
      onSuccess: (data) => {
        // Cache the current page data
        if (syncuserView && syncuserView.length > 0) {
          setCachedPages((prev) => ({ ...prev, [currentPage]: [...syncuserView] }));
        }

        // setHasNextPage(apiHasNextPage || false);
      },
      onError: (error: any) => {
        let errorMessage = 'We are unable to get the user from Google';

        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        openNotification('error', errorMessage);
      },
    }
  );

  const getDepartmentsReq = useQuery('getDepartmentsReq', () => syncDepartment(), {
    onError: () => openNotification('error', `Can't get departments`),
  });

  const handleInputChange = async (input: string) => {
    setCurrentPage(0);
    setNextPageToken('');
    setPageTokens(['']);
    setCachedPages({ 0: [] });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = useCallback(
    debounce((input) => handleInputChange(input), 500),
    []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  const handleChange = (event: any) => {
    const input = event.target.value;
    setSearch(input);
    if (input === '') {
      setDepartment('');
      setNextPageToken('');
      setCurrentPage(0);
      setPageTokens(['']);
      setCachedPages({ 0: [] });
      // Use setTimeout to ensure state updates are applied before triggering refresh
      setTimeout(() => {
        setRefreshKey((prev) => prev + 1);
      }, 50);
    } else {
      debouncedInputChange(input);
    }
  };

  const rows = useMemo(() => {
    // If search or department is active, always use fresh data from syncuserView
    if (search || department) {
      return !syncUserReq.isFetching ? syncuserView : [];
    }

    // If we have cached data for current page and we're going backward, use cached data
    if (cachedPages[currentPage] && cachedPages[currentPage].length > 0) {
      return cachedPages[currentPage];
    }

    return !syncUserReq.isFetching ? syncuserView : [];
  }, [syncUserReq.isFetching, syncuserView, cachedPages, currentPage, search, department]);

  const handleSelectionModelChange = (params: GridSelectionModelChangeParams) => {
    const selectedIds = params.selectionModel as number[];
    const selectedData = rows.filter((row: any) => selectedIds.includes(row.id));
    const emails = selectedData
      .map((item: any) => item.primaryEmail)
      .filter((email: any): email is string => email !== undefined);
    setSelectedEmail(emails);
  };

  const handleApiCall = (selectedValue: string) => {
    setDepartment(selectedValue);
    setCurrentPage(0);
    setNextPageToken('');
    setPageTokens(['']);
    setCachedPages({ 0: [] });
  };
  const handlePageChange = (params: any) => {
    const { page, pageSize: newPageSize } = params;
    setPageSize(newPageSize);

    const newPage = page.page || page;

    if (newPage > currentPage && (apiHasNextPage || syncToken)) {
      setCurrentPage(newPage);
      const token = syncToken || '';
      setNextPageToken(token);

      // Store token for this page if not already stored
      if (pageTokens.length <= newPage) {
        setPageTokens([...pageTokens, token]);
      }
    } else if (newPage < currentPage) {
      setCurrentPage(newPage);

      // For page 0, always use empty token
      if (newPage === 0) {
        setNextPageToken('');
      } else {
        const token = pageTokens[newPage] || '';
        setNextPageToken(token);
      }
    } else if (newPage === currentPage && newPageSize !== pageSize) {
      setCurrentPage(0);
      setNextPageToken('');
      setPageTokens(['']);
      setCachedPages({ 0: [] });
    }
  };
  const handleClose = () => {
    setSearch('');
    setDepartment('');
    setCurrentPage(0);
    setNextPageToken('');
    setPageTokens(['']);
    setCachedPages({ 0: [] });
    // Use setTimeout to ensure state updates are applied before triggering refresh
    setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 50);
  };

  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const getStatisticReq = useQuery('getStatisticReq', () => getStatistic(), {
    onError: () => openNotification('error', `Can't get statistic`),
  });
  const reSyncUserReq = useMutation(() => reSyncUser(), {
    mutationKey: 'reSyncUser',
    onSuccess: () => {
      setOpen(false);
      const syncedUserIds = new Set(
        syncuserView.map((user) => user.googleId).filter((id): id is string => id !== null)
      );
      setSyncedUsers(syncedUserIds);
      setLoadingMessage(null);
      openNotification('success', 'Bi-direction Sync Successful');
      syncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      setLoadingMessage(null);
      let errorMessage = 'Sync failed';

      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      openNotification('error', errorMessage);
    },
  });
  const reSyncSingleUserReq = useMutation((data: { email: string; googleId: string }) => reSyncSingleUser(data), {
    mutationKey: 'reSyncSingleUserReq',
    onSuccess: (_, variables) => {
      openNotification('success', 'Selected Users sync’ed with App Google Successfully');
      setSyncedUsers((prev) => new Set(prev).add(variables.googleId));
      syncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      let errorMessage = 'Single user sync failed';

      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      openNotification('error', errorMessage);
    },
  });
  const selectedUserReq = useMutation((data: { emails: string[] }) => SelectedUser(data), {
    mutationKey: 'selectedUserReq',
    onSuccess: () => {
      openNotification('success', 'Selected Users synced with App Google Successfully');
      setSelectedEmail([]);
      syncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      setOpen(false);
      openNotification('error', errorMessage);
    },
  });
  // useEffect(() => {
  //   setHasNextPage(apiHasNextPage || false);
  // }, [apiHasNextPage]);

  useEffect(() => {
    if (maxResults && maxResults !== pageSize) {
      setPageSize(maxResults);
    }
  }, [maxResults, pageSize]);

  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncUser';
  }, []);

  const user = usersView.map((item: { googleId: any }) => item.googleId);
  const adminUser = adminsView.map((item: { googleId: any }) => item.googleId);
  const filteredUserSet = new Set(user);
  const filteredAdminUserSet = new Set(adminUser);
  const [syncedUsers, setSyncedUsers] = useState<Set<string>>(new Set());

  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const handleSync = (email: string, googleId: string) => {
    reSyncSingleUserReq.mutate({ email, googleId });
  };
  const handleSyncAll = () => {
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    reSyncUserReq.mutate();
    setOpen(true);
  };
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <PersonIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'creationTimestamp',
      headerName: 'CREATED',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.creationTimestamp)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.creationTimestamp)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'lastLoginTime',
      headerName: 'UPDATED',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.lastLoginTime)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.lastLoginTime)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.name || ''}>
          <div>
            <span>{params.row.name}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'isSuspended',
      headerName: 'STATUS',
      flex: 0.4,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.isSuspended === false ? 'Yes' : 'No'}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {params.row.isSuspended === false ? (
              <>
                <CircularProgress
                  variant='determinate'
                  value={100}
                  size={40}
                  style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                />
                <CheckCircleIcon
                  style={{
                    color: 'green',
                    fontSize: '24px',
                    zIndex: 2,
                    margin: '8px',
                  }}
                />
              </>
            ) : (
              <>
                <CircularProgress
                  variant='determinate'
                  value={100}
                  size={40}
                  style={{ color: 'grey', position: 'absolute', zIndex: 1 }}
                />
                <CloseIcon
                  style={{
                    color: 'red',
                    fontSize: '24px',
                    zIndex: 2,
                    margin: '8px',
                  }}
                />
              </>
            )}
          </div>
        </Tooltip>
      ),
    },
    // {
    //   field: 'mail',
    //   headerName: 'EMAIL',
    //   flex: 1,
    //   renderCell: (params: GridCellParams) => (
    //     <Tooltip title={params.value|| ''} placement='bottom'>
    //       <div style={{ display: 'flex', gap: '10px' }}>
    //         <img src={EmailIcon} alt='' />
    //         <span>{params.value}</span>
    //       </div>
    //     </Tooltip>
    //   ),
    // },
    {
      field: 'primaryEmail',
      headerName: 'PRIMARY EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        const isNotSynced = !filteredUserSet.has(params.row.googleId) && !syncedUsers.has(params.row.googleId);
        const isAdminNotSynced =
          !filteredAdminUserSet.has(params.row.googleId) && !syncedUsers.has(params.row.googleId);
        const isSyncDisabled = !(isNotSynced && isAdminNotSynced);
        return (
          <>
            {!isSyncDisabled ? (
              <Tooltip title='Not Sync'>
                <IconButton size='small' onClick={() => handleSync(params.row.primaryEmail, params.row.googleId)}>
                  <SyncDisabledIcon style={{ color: red[500] }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title='Sync'>
                <IconButton size='small' onClick={() => handleSync(params.row.primaryEmail, params.row.googleId)}>
                  <SyncIcon style={{ color: green[500] }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const ldapUserCount = usersView.length ?? 0;
  const disabledSyncCount = rows
    .filter((row: any) => row.googleId !== null)
    .filter((row: any) => row.googleId && !filteredUserSet.has(row.googleId) && !syncedUsers.has(row.googleId)).length;

  useAppPreloader(
    syncUserReq.isFetching ||
      reSyncUserReq.isLoading ||
      getUsersReq.isFetching ||
      reSyncSingleUserReq.isLoading ||
      getStatisticReq.isFetching ||
      selectedUserReq.isLoading ||
      getDepartmentsReq.isFetching ||
      getAdminUsersReq.isFetching
  );

  return (
    <>
      {!syncUserReq.isFetching && !getUsersReq.isFetching && !getStatisticReq.isFetching && (
        <Paper className={classes.dataGrid} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapUserCount} Users in EasyIdentity.</h1>
            {/* <h1 className={classes.textBox}>You have {googleUserCount} Users in Google Workspace</h1> */}
            <h1 className={classes.textBox}>We have found users in your Google Workspace.</h1>
            <h1 className={classes.textBox}>
              Sync Users : EasyIdentity {'<->'} Google Workspace
              <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
            </h1>
            {disabledSyncCount > Number(generalStatistic?.leftAccounts) && (
              <h1 className={classes.textBox}>
                There are only {generalStatistic?.leftAccounts || '0'} Easyidentity User Licenses remaining. Please
                upgrade your subscription to Sync Easyidentity User Accounts with Google Workspace.
              </h1>
            )}
          </>
        </Paper>
      )}
      <Paper className={classes.dataGrid} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>
            List of All Google Workspace Users
          </h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className={classes.centerHeadBox}>
            <TextField
              className={classes.searchInput}
              placeholder='Search'
              value={search}
              onChange={handleChange}
              InputProps={{
                startAdornment: <SearchIcon style={{ color: 'grey' }} />,
                endAdornment: (
                  <InputAdornment position='end'>
                    {search ? (
                      <IconButton size='small' onClick={handleClose}>
                        <ClearIcon style={{ color: 'red' }} />
                      </IconButton>
                    ) : (
                      ' '
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div style={{ flexGrow: 1 }}>
            {allsyncDepartmentsView.length === 0 ? (
              <div>
                <span>No Departments Found.</span>
              </div>
            ) : (
              <Controller
                name='base'
                key='base'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    className={classes.inputTwo}
                    options={allsyncDepartmentsView}
                    renderInput={(params) => <TextField {...params} variant='outlined' className={classes.inputTwo} />}
                    getOptionLabel={(option) => (option.ou ? option.ou.replace(/^\//, '') : '')}
                    onChange={(e, value) => {
                      const selectedValue = value ? value.ou.replace(/^\//, '') : '';
                      field.onChange(selectedValue);
                      handleApiCall(selectedValue);
                    }}
                    value={
                      allsyncDepartmentsView.find((option) => option.ou.replace(/^\//, '') === field.value) || null
                    }
                  />
                )}
              />
            )}
          </div>
          <Button
            onClick={() => {
              selectedUserReq.mutate({ emails: selectedEmail });
            }}
            variant='contained'
            color='primary'
            disabled={selectedEmail.length === 0}
          >
            Sync
          </Button>
        </div>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          paginationMode='server'
          page={currentPage}
          onPageChange={(newPage) => handlePageChange({ page: newPage, pageSize })}
          onPageSizeChange={(newPageSize) => handlePageChange({ page: 0, pageSize: newPageSize })}
          rowCount={totalUserCount}
          checkboxSelection
          onSelectionModelChange={handleSelectionModelChange}
        />
      </Paper>

      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>
    </>
  );
});
